import { useState, useCallback } from 'react';
import { TextField } from '@mui/material';
import type { TextFieldProps } from '@mui/material';

type Props = Omit<TextFieldProps, 'value' | 'onChange'> & {
  value: number | undefined | null;
  onChange: (value: number | undefined) => void;
  currency?: string;
};

const formatDisplay = (n: number, currency: string) =>
  `${currency}${n.toLocaleString('en-NG')}`;

export const CurrencyInput = ({ value, onChange, currency = '₦', ...rest }: Props) => {
  const [focused, setFocused] = useState(false);
  const [raw, setRaw] = useState('');

  const handleFocus = useCallback(() => {
    setFocused(true);
    setRaw(value != null && value !== 0 ? String(value) : '');
  }, [value]);

  const handleBlur = useCallback(() => {
    setFocused(false);
    const num = Number.parseFloat(raw.replace(/,/g, ''));
    onChange(Number.isFinite(num) ? num : undefined);
  }, [raw, onChange]);

  const handleChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const input = e.target.value.replace(/[^0-9.]/g, '');
    setRaw(input);
    const num = Number.parseFloat(input);
    if (Number.isFinite(num)) onChange(num);
    else if (input === '' || input === '.') onChange(undefined);
  }, [onChange]);

  const displayValue = focused
    ? raw
    : value != null && value !== 0
      ? formatDisplay(value, currency)
      : '';

  return (
    <TextField
      {...rest}
      value={displayValue}
      onChange={handleChange}
      onFocus={handleFocus}
      onBlur={handleBlur}
      inputProps={{ ...rest.inputProps, inputMode: 'decimal' }}
    />
  );
};
